<template>
	<div class="date-filter">
		<div class="date-picker-wrap">
			<div class="date-picker-wrap2">
				<VueDatePicker
					v-model="range"
					:placeholder="placeholder"
					:range="mode === 'range'"
					:enable-time-picker="false"
					:format="formatDate"
					:text-input="true"
					:auto-apply="true"
					class="date-input" />
			</div>

			<span class="clear" v-if="hasValue" @click="clear">X</span>
		</div>
	</div>
</template>

<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { ref, watch, computed } from "vue";

export default {
	name: "DateFilter",
	components: {
		VueDatePicker,
	},
	props: {
		placeholder: {
			type: String,
			default: "Filter by date",
		},
		mode: {
			type: String,
			default: "range",
		},
		value: {
			type: [String, Array, Number, Date, Object],
			default: "",
		},
		fixed: {
			type: Boolean,
			default: false,
		},
	},
	setup(props, { emit }) {
		const range = ref(null);

		// Initialize range based on props.value and mode
		if (props.value !== "") {
			if (props.mode === "range") {
				// For range mode, expect an object with start/end or array with two dates
				if (typeof props.value === "object" && props.value.start && props.value.end) {
					range.value = [new Date(props.value.start), new Date(props.value.end)];
				} else if (Array.isArray(props.value) && props.value.length === 2) {
					range.value = [new Date(props.value[0]), new Date(props.value[1])];
				}
			} else {
				// For single mode, expect a single date
				range.value = typeof props.value === "string" ? new Date(props.value) : props.value;
			}
		}

		watch(
			range,
			(newValue, oldValue) => {
				if (newValue === null || oldValue === null) {
					emit("change", newValue);
				} else {
					if (props.mode === "range") {
						// For range mode, newValue is an array [start, end]
						if (Array.isArray(newValue) && newValue.length === 2) {
							emit("change", {
								start: newValue[0],
								end: newValue[1],
							});
						}
					} else {
						// For single mode, newValue is a single Date
						emit("change", newValue);
					}
				}
			},
			{ deep: true }
		);

		const hasValue = computed(() => {
			if (!range.value) return false;
			if (props.mode === "range") {
				return Array.isArray(range.value) && range.value.length === 2 && range.value[0] && range.value[1];
			}
			return range.value !== null && range.value !== undefined;
		});

		const action = (a) => {
			emit(a);
		};

		const clear = () => {
			range.value = null;
			action("clear");
		};

		const formatDate = (date) => {
			if (!date) return "";
			if (Array.isArray(date)) {
				// For range mode - date is an array of [start, end]
				return date.map((d) => (d instanceof Date ? d.toLocaleDateString() : "")).join(" - ");
			}
			if (date instanceof Date) {
				return date.toLocaleDateString();
			}
			if (typeof date === "string") return date;
			return "";
		};

		return {
			range,
			hasValue,
			clear,
			formatDate,
		};
	},
};
</script>
<style lang="less" scoped>
.date-filter {
	.date-picker-wrap {
		width: 100%;

		.vc-border {
			border-width: 0px;
		}

		.date-picker-wrap2 {
			display: inline-block;
		}
	}
}
</style>
<style lang="less">
@import "./../../assets/css/less/_settings.less";
.date-filter {
	.date-picker-wrap {
		position: relative;

		.clear {
			cursor: pointer;
			font-size: 80%;
			color: @formDefaultColour;
			display: block;
			position: absolute;
			top: 9px;
			right: 6px;
			z-index: 10;
			&:hover {
				text-decoration: underline;
			}
		}

		input,
		.date-input {
			border: 1px solid rgba(0, 0, 0, 0.1);
			padding: 6px 20px 6px 6px;
			width: 100%;
			cursor: pointer;
		}

		// Vue Datepicker specific styles
		.dp__input_wrap input {
			border: 1px solid rgba(0, 0, 0, 0.1);
			padding: 6px 20px 6px 6px;
			width: 100%;
			cursor: pointer;
			background-color: @surfaceInputActive;
		}

		.dp__menu {
			left: -30px !important;
		}

		.date-picker-wrap2 {
			width: 100%;
			margin-top: 3px;
		}
	}

	&.table-filter {
		.date-picker-wrap {
			input,
			.dp__input_wrap input {
				background-color: @surfaceInputActive;
				-webkit-font-smoothing: antialiased;
				-moz-osx-font-smoothing: grayscale;
			}
		}
	}
}

@media screen and (min-width: 1024px) {
	.date-filter {
		.date-picker-wrap {
			.vc-popover-content-wrapper {
				left: -30px !important;
			}
		}
	}
}
</style>